import { getFunctions, httpsCallable } from 'firebase/functions';
import { PHIDataType } from '@/models/audit-log.model';
import { RiskSeverity, RiskLikelihood } from '@/models/team-risk.model';

export interface PHIFlag {
  type: PHIDataType;
  confidence: 'low' | 'medium' | 'high';
  location: string;
  snippet?: string;
  recommendedAction: 'review' | 'redact' | 'warn';
}

export interface ProcessTranscriptRequest {
  meetingId: string;
  transcriptText: string;
}

export interface ProcessTranscriptResponse {
  success: boolean;
  meetingId: string;
  decisionsCount: number;
  tasksCount: number;
  risksCount: number;
  questionsCount: number;
  phiDetected: boolean;
  phiFlags?: PHIFlag[];
  error?: string;
}

/**
 * AI Workspace Service
 * Client-side service for AI-powered workspace features
 *
 * Note: The actual AI processing happens server-side in Firebase Cloud Functions
 * using Vertex AI (Claude 3.5 Sonnet). This service provides client-side utilities
 * and wrappers for calling the Cloud Functions.
 */
export class AIWorkspaceService {
  /**
   * Process a meeting transcript using Cloud Function with Vertex AI
   *
   * This calls the Firebase Cloud Function which uses Vertex AI (Claude 3.5 Sonnet)
   * to extract decisions, tasks, risks, and questions from the transcript.
   *
   * The Cloud Function uses Application Default Credentials (no API key needed).
   */
  static async processTranscript(
    meetingId: string,
    transcriptText: string
  ): Promise<ProcessTranscriptResponse> {
    try {
      const functions = getFunctions();
      const processTranscriptFunction = httpsCallable<
        ProcessTranscriptRequest,
        ProcessTranscriptResponse
      >(functions, 'processTranscript');

      const result = await processTranscriptFunction({
        meetingId,
        transcriptText,
      });

      return result.data;
    } catch (error: any) {
      console.error('Error processing transcript:', error);
      return {
        success: false,
        meetingId,
        decisionsCount: 0,
        tasksCount: 0,
        risksCount: 0,
        questionsCount: 0,
        phiDetected: false,
        error: error.message || 'Unknown error occurred',
      };
    }
  }

  /**
   * Client-side PHI detection (pattern-based)
   * This runs before the Cloud Function as a pre-check
   */
  static detectPHI(text: string): PHIFlag[] {
    const flags: PHIFlag[] = [];

    // SSN pattern: XXX-XX-XXXX
    const ssnPattern = /\b\d{3}-\d{2}-\d{4}\b/g;
    const ssnMatches = text.match(ssnPattern);
    if (ssnMatches && ssnMatches.length > 0) {
      flags.push({
        type: PHIDataType.ssn,
        confidence: 'high',
        location: 'Multiple locations',
        snippet: ssnMatches[0],
        recommendedAction: 'redact',
      });
    }

    // Phone numbers: (XXX) XXX-XXXX or XXX-XXX-XXXX
    const phonePattern = /\b(\(\d{3}\)\s*\d{3}-\d{4}|\d{3}-\d{3}-\d{4})\b/g;
    const phoneMatches = text.match(phonePattern);
    if (phoneMatches && phoneMatches.length > 2) {
      // Only flag if multiple phone numbers (could be business)
      flags.push({
        type: PHIDataType.phoneNumber,
        confidence: 'medium',
        location: 'Throughout transcript',
        recommendedAction: 'review',
      });
    }

    // Email addresses
    const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    const emailMatches = text.match(emailPattern);
    if (emailMatches && emailMatches.length > 3) {
      // Only flag if many emails (could be business)
      flags.push({
        type: PHIDataType.email,
        confidence: 'low',
        location: 'Throughout transcript',
        recommendedAction: 'review',
      });
    }

    // Date of birth patterns: MM/DD/YYYY or MM-DD-YYYY
    const dobPattern = /\b(0?[1-9]|1[0-2])[\/\-](0?[1-9]|[12][0-9]|3[01])[\/\-](19|20)\d{2}\b/g;
    const dobMatches = text.match(dobPattern);
    if (dobMatches && dobMatches.length > 0) {
      flags.push({
        type: PHIDataType.dateOfBirth,
        confidence: 'medium',
        location: 'Date references found',
        recommendedAction: 'review',
      });
    }

    // Medical record numbers: MRN followed by digits
    const mrnPattern = /\b(MRN|mrn|medical record|patient id)[:\s]+\d+\b/gi;
    const mrnMatches = text.match(mrnPattern);
    if (mrnMatches && mrnMatches.length > 0) {
      flags.push({
        type: PHIDataType.medicalRecordNumber,
        confidence: 'high',
        location: 'Medical record references found',
        recommendedAction: 'redact',
      });
    }

    // Health insurance numbers
    const insurancePattern = /\b(insurance|policy|member)\s*(number|id|#)[:\s]+[\w\d-]+\b/gi;
    const insuranceMatches = text.match(insurancePattern);
    if (insuranceMatches && insuranceMatches.length > 0) {
      flags.push({
        type: PHIDataType.healthInsuranceNumber,
        confidence: 'high',
        location: 'Insurance references found',
        recommendedAction: 'redact',
      });
    }

    // Medical keywords detection
    const medicalKeywords = [
      'diagnosis',
      'patient',
      'treatment',
      'prescription',
      'medication',
      'surgery',
      'doctor',
      'physician',
      'hospital',
      'clinic',
      'medical record',
      'health condition',
      'disease',
      'illness',
      'symptom',
      'procedure',
      'therapy',
      'lab results',
      'test results',
      'blood work',
      'x-ray',
      'MRI',
      'CT scan',
    ];

    const lowerText = text.toLowerCase();
    let medicalKeywordCount = 0;
    const foundKeywords: string[] = [];

    medicalKeywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      const matches = lowerText.match(regex);
      if (matches) {
        medicalKeywordCount += matches.length;
        if (!foundKeywords.includes(keyword)) {
          foundKeywords.push(keyword);
        }
      }
    });

    // Flag if many medical keywords found
    if (medicalKeywordCount > 5 || foundKeywords.length > 3) {
      flags.push({
        type: PHIDataType.healthRecord,
        confidence: medicalKeywordCount > 10 ? 'high' : 'medium',
        location: 'Throughout transcript',
        snippet: `Medical keywords: ${foundKeywords.slice(0, 3).join(', ')}${foundKeywords.length > 3 ? '...' : ''}`,
        recommendedAction: medicalKeywordCount > 10 ? 'redact' : 'review',
      });
    }

    // HIPAA-specific protected health information keywords
    const phiKeywords = [
      'protected health information',
      'PHI',
      'HIPAA',
      'patient confidentiality',
      'medical privacy',
      'health privacy',
    ];

    let phiKeywordCount = 0;
    phiKeywords.forEach(keyword => {
      if (lowerText.includes(keyword.toLowerCase())) {
        phiKeywordCount++;
      }
    });

    if (phiKeywordCount > 0) {
      flags.push({
        type: PHIDataType.healthRecord,
        confidence: 'high',
        location: 'PHI/HIPAA references found',
        recommendedAction: 'redact',
      });
    }

    // Check for names with medical context
    // Pattern: Dr. [Name], [Name] the patient, patient [Name]
    const medicalNamePattern = /(Dr\.|Doctor|patient)\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/g;
    const medicalNameMatches = text.match(medicalNamePattern);
    if (medicalNameMatches && medicalNameMatches.length > 0) {
      flags.push({
        type: PHIDataType.name,
        confidence: 'high',
        location: 'Patient/doctor names found',
        snippet: medicalNameMatches[0],
        recommendedAction: 'redact',
      });
    }

    // Check for addresses (street addresses)
    const addressPattern = /\b\d+\s+[A-Z][a-z]+\s+(Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr)\b/g;
    const addressMatches = text.match(addressPattern);
    if (addressMatches && addressMatches.length > 0) {
      flags.push({
        type: PHIDataType.address,
        confidence: 'medium',
        location: 'Address references found',
        recommendedAction: 'review',
      });
    }

    return flags;
  }

  /**
   * Calculate risk score from severity and likelihood
   */
  static calculateRiskScore(
    severity: RiskSeverity,
    likelihood: RiskLikelihood
  ): number {
    const severityValues: Record<RiskSeverity, number> = {
      [RiskSeverity.low]: 1,
      [RiskSeverity.medium]: 2,
      [RiskSeverity.high]: 3,
      [RiskSeverity.critical]: 4,
    };

    const likelihoodValues: Record<RiskLikelihood, number> = {
      [RiskLikelihood.veryLow]: 1,
      [RiskLikelihood.low]: 2,
      [RiskLikelihood.medium]: 3,
      [RiskLikelihood.high]: 4,
      [RiskLikelihood.veryHigh]: 5,
    };

    return severityValues[severity] * likelihoodValues[likelihood];
  }

  /**
   * Get risk level based on risk score
   */
  static getRiskLevel(riskScore: number): 'low' | 'medium' | 'high' | 'critical' {
    if (riskScore >= 15) return 'critical';
    if (riskScore >= 10) return 'high';
    if (riskScore >= 6) return 'medium';
    return 'low';
  }

  /**
   * Redact PHI from text
   * Simple redaction - replaces patterns with [REDACTED]
   */
  static redactPHI(text: string, phiFlags: PHIFlag[]): string {
    let redactedText = text;

    phiFlags.forEach(flag => {
      if (flag.recommendedAction === 'redact') {
        switch (flag.type) {
          case PHIDataType.ssn:
            redactedText = redactedText.replace(
              /\b\d{3}-\d{2}-\d{4}\b/g,
              '[REDACTED-SSN]'
            );
            break;

          case PHIDataType.phoneNumber:
            redactedText = redactedText.replace(
              /\b(\(\d{3}\)\s*\d{3}-\d{4}|\d{3}-\d{3}-\d{4})\b/g,
              '[REDACTED-PHONE]'
            );
            break;

          case PHIDataType.email:
            redactedText = redactedText.replace(
              /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
              '[REDACTED-EMAIL]'
            );
            break;

          case PHIDataType.medicalRecordNumber:
            redactedText = redactedText.replace(
              /\b(MRN|mrn|medical record|patient id)[:\s]+\d+\b/gi,
              '[REDACTED-MRN]'
            );
            break;

          case PHIDataType.healthInsuranceNumber:
            redactedText = redactedText.replace(
              /\b(insurance|policy|member)\s*(number|id|#)[:\s]+[\w\d-]+\b/gi,
              '[REDACTED-INSURANCE]'
            );
            break;

          default:
            break;
        }
      }
    });

    return redactedText;
  }

  /**
   * Validate transcript before processing
   * Returns validation result with any issues
   */
  static validateTranscript(transcriptText: string): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check minimum length
    if (!transcriptText || transcriptText.trim().length === 0) {
      errors.push('Transcript is empty');
    } else if (transcriptText.trim().length < 50) {
      errors.push('Transcript is too short (minimum 50 characters)');
    }

    // Check maximum length (500KB)
    const maxLength = 500 * 1024; // 500KB
    if (transcriptText.length > maxLength) {
      errors.push(`Transcript is too long (maximum ${maxLength} characters)`);
    }

    // Check for suspicious patterns
    const suspiciousPatterns = [
      { pattern: /<script/gi, message: 'Transcript contains script tags' },
      { pattern: /<iframe/gi, message: 'Transcript contains iframe tags' },
      { pattern: /javascript:/gi, message: 'Transcript contains javascript: protocol' },
    ];

    suspiciousPatterns.forEach(({ pattern, message }) => {
      if (pattern.test(transcriptText)) {
        errors.push(message);
      }
    });

    // Check for very long words (possible encoding issues)
    const words = transcriptText.split(/\s+/);
    const longWords = words.filter(w => w.length > 100);
    if (longWords.length > 0) {
      warnings.push('Transcript contains very long words, possibly encoding issues');
    }

    // Check for excessive special characters
    const specialCharCount = (transcriptText.match(/[^\w\s.,!?;:()\-'"]/g) || []).length;
    const specialCharRatio = specialCharCount / transcriptText.length;
    if (specialCharRatio > 0.1) {
      warnings.push('Transcript contains many special characters');
    }

    // Warn about PHI detection
    const phiFlags = this.detectPHI(transcriptText);
    if (phiFlags.length > 0) {
      warnings.push(
        `Potential PHI detected (${phiFlags.length} flag${phiFlags.length > 1 ? 's' : ''})`
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Extract estimated processing time based on transcript length
   */
  static estimateProcessingTime(transcriptText: string): number {
    // Rough estimate: 1 second per 100 words, minimum 5 seconds
    const wordCount = transcriptText.split(/\s+/).length;
    const estimatedSeconds = Math.max(5, Math.ceil(wordCount / 100));
    return estimatedSeconds;
  }

  /**
   * Get PHI summary from flags
   */
  static getPHISummary(phiFlags: PHIFlag[]): string {
    if (phiFlags.length === 0) {
      return 'No PHI detected';
    }

    const typeCounts: Record<string, number> = {};
    phiFlags.forEach(flag => {
      typeCounts[flag.type] = (typeCounts[flag.type] || 0) + 1;
    });

    const summary = Object.entries(typeCounts)
      .map(([type, count]) => `${count} ${type}${count > 1 ? 's' : ''}`)
      .join(', ');

    return `PHI detected: ${summary}`;
  }
}
